import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Search,
  Command,
  FileText,
  Target,
  Users,
  BarChart3,
  Settings,
  Plus,
  Zap,
  Globe,
  MessageSquare,
  X
} from 'lucide-react';

interface CommandItem {
  id: string;
  title: string;
  description?: string;
  icon: React.ReactNode;
  action?: () => void;
  href?: string;
  shortcut?: string;
  category: 'navigation' | 'action' | 'recent';
}

interface CommandBarProps {
  isOpen?: boolean;
  onClose?: () => void;
  placeholder?: string;
}

const CommandBar: React.FC<CommandBarProps> = ({
  isOpen: controlledIsOpen,
  onClose,
  placeholder
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const isControlled = controlledIsOpen !== undefined;
  const showModal = isControlled ? controlledIsOpen : isOpen;

  const commands: CommandItem[] = [
    // Actions
    {
      id: 'create-campaign',
      title: t('commandBar.newCampaign', 'Create new campaign'),
      icon: <Plus className="h-4 w-4" />,
      href: '/campaigns/new',
      category: 'action',
      shortcut: 'N'
    },
    {
      id: 'generate-content',
      title: t('commandBar.generateAI', 'Generate content with AI'),
      icon: <Zap className="h-4 w-4" />,
      href: '/content/generate',
      category: 'action',
      shortcut: 'G'
    },
    {
      id: 'create-post',
      title: t('commandBar.newPost', 'Create new post'),
      icon: <MessageSquare className="h-4 w-4" />,
      href: '/social',
      category: 'action'
    },
    // Navigation
    {
      id: 'dashboard',
      title: 'Dashboard',
      icon: <BarChart3 className="h-4 w-4" />,
      href: '/dashboard',
      category: 'navigation'
    },
    {
      id: 'campaigns',
      title: t('nav.campaigns', 'Campaigns'),
      icon: <Target className="h-4 w-4" />,
      href: '/campaigns',
      category: 'navigation'
    },
    {
      id: 'content',
      title: t('nav.content', 'Content'),
      icon: <FileText className="h-4 w-4" />,
      href: '/content',
      category: 'navigation'
    },
    {
      id: 'competitors',
      title: t('nav.competitors', 'Competitors'),
      icon: <Users className="h-4 w-4" />,
      href: '/competitors',
      category: 'navigation'
    },
    {
      id: 'social',
      title: t('nav.social', 'Social Media'),
      icon: <Globe className="h-4 w-4" />,
      href: '/social',
      category: 'navigation'
    },
    {
      id: 'settings',
      title: t('nav.settings', 'Settings'),
      icon: <Settings className="h-4 w-4" />,
      href: '/settings',
      category: 'navigation'
    }
  ];

  const filteredCommands = commands.filter(cmd =>
    cmd.title.toLowerCase().includes(query.toLowerCase())
  );

  const groupedCommands = {
    action: filteredCommands.filter(c => c.category === 'action'),
    navigation: filteredCommands.filter(c => c.category === 'navigation')
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Open with Cmd+K or Ctrl+K
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (isControlled) {
          // Let parent handle state
        } else {
          setIsOpen(true);
        }
      }

      // Close with Escape
      if (e.key === 'Escape' && showModal) {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showModal, isControlled]);

  useEffect(() => {
    if (showModal) {
      inputRef.current?.focus();
      setQuery('');
      setSelectedIndex(0);
    }
  }, [showModal]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const handleClose = () => {
    if (isControlled && onClose) {
      onClose();
    } else {
      setIsOpen(false);
    }
    setQuery('');
  };

  const handleSelect = (item: CommandItem) => {
    if (item.action) {
      item.action();
    } else if (item.href) {
      navigate(item.href);
    }
    handleClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const allItems = [...groupedCommands.action, ...groupedCommands.navigation];

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, allItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = allItems[selectedIndex];
      if (item) handleSelect(item);
    }
  };

  if (!showModal) return null;

  const allItems = [...groupedCommands.action, ...groupedCommands.navigation];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative min-h-screen flex items-start justify-center pt-[15vh] px-4">
        <div className="relative w-full max-w-xl bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Search Input */}
          <div className="flex items-center px-4 border-b border-gray-200 dark:border-gray-700">
            <Search className="h-5 w-5 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder || t('commandBar.placeholder', 'Search or type a command...')}
              className="flex-1 px-4 py-4 text-gray-900 dark:text-white bg-transparent border-0 focus:outline-none focus:ring-0"
            />
            <div className="flex items-center space-x-2">
              <kbd className="hidden sm:inline-flex items-center px-2 py-1 text-xs font-medium text-gray-400 bg-gray-100 dark:bg-gray-700 rounded">
                ESC
              </kbd>
              <button
                onClick={handleClose}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 sm:hidden"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Results */}
          <div className="max-h-[400px] overflow-y-auto py-2">
            {filteredCommands.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                {t('commandBar.noResults', 'No results found')}
              </div>
            ) : (
              <>
                {/* Actions */}
                {groupedCommands.action.length > 0 && (
                  <div className="px-2 py-2">
                    <div className="px-2 py-1 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('commandBar.actions', 'Actions')}
                    </div>
                    {groupedCommands.action.map((item, idx) => (
                      <button
                        key={item.id}
                        onClick={() => handleSelect(item)}
                        className={`
                          w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors
                          ${selectedIndex === idx
                            ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                            : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                          }
                        `}
                      >
                        <div className={`
                          w-8 h-8 rounded-lg flex items-center justify-center
                          ${selectedIndex === idx
                            ? 'bg-primary-100 dark:bg-primary-800'
                            : 'bg-gray-100 dark:bg-gray-700'
                          }
                        `}>
                          {item.icon}
                        </div>
                        <span className="flex-1 text-left font-medium">
                          {item.title}
                        </span>
                        {item.shortcut && (
                          <kbd className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 rounded">
                            {item.shortcut}
                          </kbd>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {/* Navigation */}
                {groupedCommands.navigation.length > 0 && (
                  <div className="px-2 py-2">
                    <div className="px-2 py-1 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('commandBar.navigation', 'Navigation')}
                    </div>
                    {groupedCommands.navigation.map((item, idx) => {
                      const globalIdx = groupedCommands.action.length + idx;
                      return (
                        <button
                          key={item.id}
                          onClick={() => handleSelect(item)}
                          className={`
                            w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors
                            ${selectedIndex === globalIdx
                              ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                              : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                            }
                          `}
                        >
                          <div className={`
                            w-8 h-8 rounded-lg flex items-center justify-center
                            ${selectedIndex === globalIdx
                              ? 'bg-primary-100 dark:bg-primary-800'
                              : 'bg-gray-100 dark:bg-gray-700'
                            }
                          `}>
                            {item.icon}
                          </div>
                          <span className="flex-1 text-left font-medium">
                            {item.title}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
              <div className="flex items-center space-x-4">
                <span className="flex items-center">
                  <kbd className="px-1.5 py-0.5 mr-1 bg-gray-200 dark:bg-gray-700 rounded text-[10px]">↑↓</kbd>
                  {t('commandBar.navigate', 'navigate')}
                </span>
                <span className="flex items-center">
                  <kbd className="px-1.5 py-0.5 mr-1 bg-gray-200 dark:bg-gray-700 rounded text-[10px]">↵</kbd>
                  {t('commandBar.select', 'select')}
                </span>
              </div>
              <span className="flex items-center">
                <Command className="h-3 w-3 mr-1" />
                {t('commandBar.shortcut', '⌘K to open')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommandBar;
